// MatrixTab.jsx - полная версия с фильтрацией по категориям и должностям (без бригад) и цветовыми разделителями категорий
import React, { useState, useEffect, useMemo } from 'react';
import EmptyState from '../../common/EmptyState';

const MatrixTab = ({ 
  positions = [], 
  brigades = [], 
  courses: propCourses = [],
  categories: propCategories = [],
  matrix: matrixData = { positions: [], brigades: [], assignments: {} },
  isLoading = false,
  onDataChange,
  onMatrixUpdate 
}) => {
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  // Состояние для фильтрации
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [localAssignments, setLocalAssignments] = useState({});
  const [updatingCells, setUpdatingCells] = useState(new Set());

  // Динамически получаем категории из курсов (как в TrainingsTab)
  const categories = useMemo(() => {
    if (propCategories && propCategories.length > 0) {
      return propCategories;
    }
    
    // Извлекаем уникальные категории из курсов
    const categoryMap = new Map();
    if (propCourses && Array.isArray(propCourses)) {
      propCourses.forEach(course => {
        const categoryId = course.category_id;
        const categoryName = course.category_name || course.category || 'Без категории';
        
        if (categoryId && !categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, {
            id: categoryId,
            name: categoryName
          });
        }
      });
    }
    return Array.from(categoryMap.values()).sort((a, b) => a.id - b.id);
  }, [propCourses, propCategories]);

  // Нормализация назначений из данных матрицы
  // Отсутствие курса в списке означает assigned: false
  const normalizeAssignments = (matrixData) => {
    const assignments = {};
    
    if (!matrixData?.positions) return assignments;
    
    matrixData.positions.forEach(position => {
      if (position.courses && Array.isArray(position.courses)) {
        position.courses.forEach(course => {
          const key = `${course.courseId}_${position.id}_position`;
          assignments[key] = true; // Присвоен только если курс есть в списке
        });
      }
    });
    
    return assignments;
  };

  // Используем данные из матрицы или переданные positions/brigades
  const displayPositions = matrixData.positions?.length > 0 ? matrixData.positions : positions;

  // Синхронизация локального состояния с пропсами
  useEffect(() => {
    if (matrixData?.positions && Array.isArray(matrixData.positions)) {
      const normalized = normalizeAssignments(matrixData);
      setLocalAssignments(normalized);
    }
  }, [matrixData?.positions]);

  // Получение названия категории по ID
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : 'Другое';
  };

  // Фильтрация курсов по выбранной категории
  const filteredCourses = useMemo(() => {
    let courses = propCourses;
    
    if (selectedCategory) {
      courses = courses.filter(course => 
        course.category_id === parseInt(selectedCategory)
      );
    }
    
    return courses;
  }, [propCourses, selectedCategory]);

  // Фильтрация должностей по поисковому запросу и выбранной должности
  const filteredPositions = useMemo(() => {
    let filtered = displayPositions;
    
    // Фильтр по выбранной должности
    if (selectedPosition) {
      filtered = filtered.filter(pos => pos.id === parseInt(selectedPosition));
    }
    
    // Фильтр по поисковому запросу
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(pos => 
        pos.name.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  }, [displayPositions, selectedPosition, searchTerm]);

  // Группировка курсов по категориям (с учетом фильтрации)
  const groupedCourses = useMemo(() => {
    const groups = {};
    
    filteredCourses.forEach(course => {
      const categoryId = course.category_id || 'other';
      const categoryName = course.category_name || getCategoryName(course.category_id) || 'Без категории';
      
      if (!groups[categoryId]) {
        groups[categoryId] = {
          id: categoryId,
          name: categoryName,
          courses: []
        };
      }
      groups[categoryId].courses.push({
        id: course.id,
        name: course.name,
        category_id: course.category_id,
        category: categoryName,
        duration: course.duration_hours ? `${course.duration_hours} ч.` : '—',
        periodicity: course.periodicity_months ? `${course.periodicity_months} мес.` : '—',
        description: course.description || ''
      });
    });

    // Сортируем группы по ID категории
    return Object.values(groups).sort((a, b) => {
      if (!isNaN(a.id) && !isNaN(b.id)) {
        return Number(a.id) - Number(b.id);
      }
      return a.name.localeCompare(b.name);
    });
  }, [filteredCourses, categories]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  const isAssigned = (courseId, targetId, targetType) => {
    const key = `${courseId}_${targetId}_${targetType}`;
    return localAssignments[key] === true;
  };

  const isUpdating = (courseId, targetId, targetType) => {
    const key = `${courseId}_${targetId}_${targetType}`;
    return updatingCells.has(key);
  };

  const handleAssignmentChange = async (courseId, targetId, targetType, currentAssigned) => {
    const newAssigned = !currentAssigned;
    const key = `${courseId}_${targetId}_${targetType}`;
    
    // Добавляем в список обновляемых
    setUpdatingCells(prev => new Set([...prev, key]));

    // Оптимистичное обновление UI
    setLocalAssignments(prev => ({
      ...prev,
      [key]: newAssigned
    }));

    if (onMatrixUpdate) {
      try {
        await onMatrixUpdate(courseId, targetId, targetType, newAssigned);
        showToast(`Курс ${newAssigned ? 'назначен' : 'отменен'} успешно`, 'success');
      } catch (err) {
        console.error('Ошибка обновления назначения:', err);
        
        // Откатываем изменения при ошибке
        setLocalAssignments(prev => {
          const newState = { ...prev };
          if (!currentAssigned) {
            delete newState[key];
          } else {
            newState[key] = currentAssigned;
          }
          return newState;
        });
        
        showToast(`Ошибка: ${err.message || 'Не удалось обновить назначение'}`, 'error');
      } finally {
        // Удаляем из списка обновляемых
        setUpdatingCells(prev => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    } else {
      // Если нет onMatrixUpdate, откатываем изменения
      setLocalAssignments(prev => {
        const newState = { ...prev };
        if (!currentAssigned) {
          delete newState[key];
        } else {
          newState[key] = currentAssigned;
        }
        return newState;
      });
      
      setUpdatingCells(prev => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      
      showToast('Обновление недоступно', 'error');
    }
  };

  // Сброс фильтра должности при изменении других фильтров
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const handlePositionChange = (value) => {
    setSelectedPosition(value);
    // При выборе должности сбрасываем поиск
    if (value) {
      setSearchTerm('');
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // При вводе поиска сбрасываем выбор должности
    if (value.trim()) {
      setSelectedPosition('');
    }
  };

  // Сброс всех фильтров
  const resetFilters = () => {
    setSelectedCategory('');
    setSelectedPosition('');
    setSearchTerm('');
  };

  const hasActiveFilters = selectedCategory || selectedPosition || searchTerm.trim();

  // Рендер чекбокса с индикацией загрузки и цветовыми разделителями категорий
  const renderCheckbox = (courseId, targetId, targetType, isCategoryBoundary = false, categoryId = null) => {
    const checked = isAssigned(courseId, targetId, targetType);
    const updating = isUpdating(courseId, targetId, targetType);
    
    return (
      <td 
        key={`${courseId}-${targetType}-${targetId}`} 
        className="checkbox-cell"
        data-category-boundary={isCategoryBoundary ? "true" : "false"}
        data-category-id={isCategoryBoundary ? categoryId : undefined}
      >
        <label className="matrix-checkbox-label">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => handleAssignmentChange(
              courseId, 
              targetId, 
              targetType,
              checked
            )}
            disabled={updating}
            className={updating ? 'updating' : ''}
          />
          {updating && (
            <span className="checkbox-spinner">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
              </svg>
            </span>
          )}
        </label>
      </td>
    );
  };

  // Общее количество отфильтрованных курсов для colspan
  const totalCourses = filteredCourses.length;

  if (isLoading) {
    return (
      <div className="matrix-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка матрицы компетенций...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="matrix-container">
      {toast.show && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-icon">{toast.type === 'success' ? '✓' : '⚠️'}</span>
          <span className="toast-message">{toast.message}</span>
        </div>
      )}

      <div className="matrix-header">
        <h2>Матрица компетенций</h2>
        <div className="matrix-actions">
          {hasActiveFilters && (
            <button 
              className="btn-secondary btn-sm" 
              onClick={resetFilters}
              title="Сбросить все фильтры"
            >
              ✕ Сбросить фильтры
            </button>
          )}
          {/* Фильтр по категориям */}
          <select
            className="filter-select"
            value={selectedCategory}
            onChange={(e) => handleCategoryChange(e.target.value)}
          >
            <option value="">Все категории</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
          
          {/* Фильтр по должности */}
          <select
            className="filter-select"
            value={selectedPosition}
            onChange={(e) => handlePositionChange(e.target.value)}
          >
            <option value="">Все должности</option>
            {displayPositions.map(pos => (
              <option key={pos.id} value={pos.id}>{pos.name}</option>
            ))}
          </select>
          
          {/* Поиск по должности */}
          <input
            type="text"
            className="search-input"
            placeholder="Поиск по должности..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>
      </div>

      {/* Статистика фильтрации */}
      {hasActiveFilters && (
        <div className="filter-stats">
          <span className="filter-stats-text">
            Отфильтровано: 
            {selectedCategory && (
              <span className="filter-badge">
                Категория: {categories.find(c => c.id === parseInt(selectedCategory))?.name}
              </span>
            )}
            {selectedPosition && (
              <span className="filter-badge">
                Должность: {displayPositions.find(p => p.id === parseInt(selectedPosition))?.name}
              </span>
            )}
            {searchTerm.trim() && (
              <span className="filter-badge">
                Поиск: "{searchTerm}"
              </span>
            )}
          </span>
        </div>
      )}

      <div className="matrix-wrapper">
        {totalCourses === 0 ? (
          <EmptyState 
            icon="📚" 
            title={selectedCategory ? "Курсы не найдены в выбранной категории" : "Курсы не найдены"}
            subtitle={selectedCategory ? "Выберите другую категорию или добавьте новый курс" : "Добавьте первый курс обучения, чтобы начать работу с матрицей компетенций"} 
          />
        ) : (
          <div className="matrix-scroll-wrapper">
            <table className="matrix-table matrix-table-compact">
              <thead>
                {/* Первая строка - названия курсов с цветовыми разделителями */}
                <tr>
                  <th className="corner-cell">Курс / Должность</th>
                  {groupedCourses.map((group, groupIndex) => 
                    group.courses.map((course, courseIndex) => (
                      <th 
                        key={course.id} 
                        className="course-header-compact" 
                        title={`${course.name}\n${course.duration}\n${course.periodicity}`}
                        data-category-boundary={courseIndex === 0 && groupIndex > 0 ? "true" : "false"}
                        data-category-id={courseIndex === 0 && groupIndex > 0 ? group.id : undefined}
                      >
                        <div className="course-name-compact">{course.name}</div>
                      </th>
                    ))
                  )}
                </tr>
                
                {/* Вторая строка - категории с цветовой индикацией */}
                <tr>
                  <th className="corner-cell"></th>
                  {groupedCourses.map((group, groupIndex) => (
                    <th 
                      key={`cat-${group.id}`} 
                      colSpan={group.courses.length} 
                      className="category-header-compact"
                      data-category={group.id}
                    >
                      {group.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Должности */}
                {filteredPositions.length > 0 ? (
                  filteredPositions.map(position => (
                    <tr key={`pos-${position.id}`}>
                      <td className="position-cell-compact">{position.name}</td>
                      {groupedCourses.map((group, groupIndex) =>
                        group.courses.map((course, courseIndex) => 
                          renderCheckbox(
                            course.id, 
                            position.id, 
                            'position',
                            courseIndex === 0 && groupIndex > 0,
                            group.id
                          )
                        )
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={totalCourses + 1} className="empty-message">
                      {searchTerm || selectedPosition ? 
                        "Должности не найдены по заданным фильтрам" : 
                        "Нет доступных должностей"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatrixTab;